import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { Group, GroupItemsBy, Item, MappedItem } from "../interfaces";

interface FormattedItem extends MappedItem {
  index: number;
}

interface FormattedGroup extends Group {
  items: FormattedItem[];
}

// Updated version of mappedItems pulled
// from search-autocomplete to included indexing
const useFormatItems = () =>
  useCallback(
    (items: Item[], initialIndex = 0) =>
      items.map((item, index) => ({
        id: uuidv4(),
        label: item.term,
        // assign sequential indexes to the items
        index: initialIndex + index,
        ...item,
      })),
    [],
  );

export const useGroupedBy = (
  items: Item[],
  groups?: Group[],
  groupItemsBy?: GroupItemsBy,
): { groupsToRender?: FormattedGroup[]; itemsToRender: FormattedItem[] } => {
  const formatItems = useFormatItems();

  if (!groups || !groupItemsBy) {
    return {
      groupsToRender: undefined,
      itemsToRender: formatItems(items),
    };
  }

  const hasIcons = groups.some((group) => group?.icon);

  const groupedItems = groups.reduce((groups, currentGroup) => {
    const filteredItems = items.filter((item) => {
      const groupingKey = groupItemsBy?.(item);
      return groupingKey === currentGroup.id;
    });

    if (!filteredItems.length) {
      return groups;
    }

    const previousGroup = groups[groups.length - 1];
    const previousItem = previousGroup?.items[previousGroup.items.length - 1];
    // the index of the previous item (which is the last item in the previous group)
    const currentItemIndex = previousItem ? previousItem.index + 1 : 0;
    const formattedItems = formatItems(filteredItems, currentItemIndex);

    return [
      ...groups,
      {
        ...currentGroup,
        items: formattedItems,
        shouldIndentContent: hasIcons,
      },
    ];
  }, [] as FormattedGroup[]);

  return {
    groupsToRender: groupedItems.filter((group) => !!group.items.length),
    itemsToRender: groupedItems.reduce(
      (items, group) => [...items, ...group.items],
      [] as FormattedItem[],
    ),
  };
};
